import type { ApiClient } from "../../api";
import { idempotencyHeaders } from "../../api/idempotency";
import {
  deleteApiV1DocumentWorkflowCounterpartyContactsByContact,
  deleteApiV1DocumentWorkflowSenderResolutionRulesByRule,
  getApiV1DocumentWorkflowCounterparties,
  getApiV1DocumentWorkflowCounterpartiesByCounterparty,
  getApiV1DocumentWorkflowMailMessages,
  getApiV1DocumentWorkflowMailMessagesByMessageCounterpartyCandidates,
  getApiV1DocumentWorkflowSenderResolutionRules,
  patchApiV1DocumentWorkflowCounterpartiesByCounterparty,
  postApiV1DocumentWorkflowCounterparties,
  postApiV1DocumentWorkflowCounterpartiesByCounterpartyContacts,
  postApiV1DocumentWorkflowMailMessagesByMessageResolveCounterparty,
  postApiV1DocumentWorkflowSenderResolutionRules,
  type GetApiV1DocumentWorkflowCounterpartiesQuery,
  type GetApiV1DocumentWorkflowCounterpartiesSuccess,
  type GetApiV1DocumentWorkflowMailMessagesByMessageCounterpartyCandidatesSuccess,
  type GetApiV1DocumentWorkflowMailMessagesQuery,
  type GetApiV1DocumentWorkflowMailMessagesSuccess,
  type GetApiV1DocumentWorkflowSenderResolutionRulesQuery,
  type GetApiV1DocumentWorkflowSenderResolutionRulesSuccess,
  type PatchApiV1DocumentWorkflowCounterpartiesByCounterpartyBody,
  type PostApiV1DocumentWorkflowCounterpartiesBody,
  type PostApiV1DocumentWorkflowCounterpartiesByCounterpartyContactsBody,
  type PostApiV1DocumentWorkflowMailMessagesByMessageResolveCounterpartyBody,
  type PostApiV1DocumentWorkflowSenderResolutionRulesBody,
} from "../../api/generated";

export type Counterparty =
  GetApiV1DocumentWorkflowCounterpartiesSuccess["data"][number];
export type CounterpartyContact = Counterparty["contacts"][number];
export type CounterpartyPage = GetApiV1DocumentWorkflowCounterpartiesSuccess;
export type CounterpartiesQuery =
  NonNullable<GetApiV1DocumentWorkflowCounterpartiesQuery>;
export type CounterpartyInput = PostApiV1DocumentWorkflowCounterpartiesBody;
export type CounterpartyPatch =
  PatchApiV1DocumentWorkflowCounterpartiesByCounterpartyBody;
export type CounterpartyContactInput =
  PostApiV1DocumentWorkflowCounterpartiesByCounterpartyContactsBody;

export type SenderRule =
  GetApiV1DocumentWorkflowSenderResolutionRulesSuccess["data"][number];
export type SenderRulePage =
  GetApiV1DocumentWorkflowSenderResolutionRulesSuccess;
export type SenderRulesQuery =
  NonNullable<GetApiV1DocumentWorkflowSenderResolutionRulesQuery>;
export type SenderRuleInput =
  PostApiV1DocumentWorkflowSenderResolutionRulesBody;

export type UnresolvedMessage =
  GetApiV1DocumentWorkflowMailMessagesSuccess["data"][number];
export type UnresolvedMessagesQuery =
  NonNullable<GetApiV1DocumentWorkflowMailMessagesQuery>;
export type SenderCandidate =
  GetApiV1DocumentWorkflowMailMessagesByMessageCounterpartyCandidatesSuccess["data"][number];
export type ResolveCounterpartyInput =
  PostApiV1DocumentWorkflowMailMessagesByMessageResolveCounterpartyBody;

/**
 * Справочник контрагентов и разбор отправителей писем.
 *
 * Только операции, которые есть в Core: удаления контрагента, правки контакта
 * и правки правила там нет, поэтому здесь их тоже нет. Домены отдельного
 * маршрута не имеют и заменяются целиком через `update`.
 */
export class CounterpartiesApi {
  constructor(private readonly client: ApiClient) {}

  list(query: CounterpartiesQuery = {}): Promise<CounterpartyPage> {
    return getApiV1DocumentWorkflowCounterparties(this.client, { query });
  }

  async get(id: string): Promise<Counterparty> {
    return (
      await getApiV1DocumentWorkflowCounterpartiesByCounterparty(this.client, {
        path: { counterparty: id },
      })
    ).data;
  }

  async create(
    input: CounterpartyInput,
    idempotencyKey?: string,
  ): Promise<Counterparty> {
    return (
      await postApiV1DocumentWorkflowCounterparties(this.client, {
        body: input,
        headers: idempotencyHeaders(idempotencyKey),
      })
    ).data;
  }

  async update(id: string, patch: CounterpartyPatch): Promise<Counterparty> {
    return (
      await patchApiV1DocumentWorkflowCounterpartiesByCounterparty(
        this.client,
        {
          path: { counterparty: id },
          body: patch,
        },
      )
    ).data;
  }

  async addContact(
    id: string,
    input: CounterpartyContactInput,
    idempotencyKey?: string,
  ): Promise<CounterpartyContact> {
    return (
      await postApiV1DocumentWorkflowCounterpartiesByCounterpartyContacts(
        this.client,
        {
          path: { counterparty: id },
          body: input,
          headers: idempotencyHeaders(idempotencyKey),
        },
      )
    ).data;
  }

  async deleteContact(contactId: string): Promise<void> {
    await deleteApiV1DocumentWorkflowCounterpartyContactsByContact(
      this.client,
      {
        path: { contact: contactId },
      },
    );
  }

  rules(query: SenderRulesQuery = {}): Promise<SenderRulePage> {
    return getApiV1DocumentWorkflowSenderResolutionRules(this.client, {
      query,
    });
  }

  async createRule(
    input: SenderRuleInput,
    idempotencyKey?: string,
  ): Promise<SenderRule> {
    return (
      await postApiV1DocumentWorkflowSenderResolutionRules(this.client, {
        body: input,
        headers: idempotencyHeaders(idempotencyKey),
      })
    ).data;
  }

  async deleteRule(ruleId: string): Promise<void> {
    await deleteApiV1DocumentWorkflowSenderResolutionRulesByRule(this.client, {
      path: { rule: ruleId },
    });
  }

  /** Письма, чей отправитель не определён или определён неоднозначно. */
  async unresolvedMessages(
    query: UnresolvedMessagesQuery = { resolution_state: "pending" },
  ): Promise<UnresolvedMessage[]> {
    return (
      await getApiV1DocumentWorkflowMailMessages(this.client, {
        query: { per_page: 50, ...query },
      })
    ).data;
  }

  async candidates(messageId: string): Promise<SenderCandidate[]> {
    return (
      await getApiV1DocumentWorkflowMailMessagesByMessageCounterpartyCandidates(
        this.client,
        { path: { message: messageId } },
      )
    ).data;
  }

  /**
   * Ручное сопоставление письма с контрагентом. Ключ идемпотентности
   * обязателен: повтор после сбоя не должен создать второе правило или
   * второй аудит-след.
   */
  resolve(
    messageId: string,
    input: ResolveCounterpartyInput,
    idempotencyKey: string,
  ) {
    return postApiV1DocumentWorkflowMailMessagesByMessageResolveCounterparty(
      this.client,
      {
        path: { message: messageId },
        body: input,
        headers: idempotencyHeaders(idempotencyKey),
      },
    );
  }
}
